import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Upload } from 'lucide-react';

interface TeamMember {
  id: string;
  name: string;
  title: string;
  credentials: string | null;
  bio: string;
  photo_url: string;
  linkedin_url: string | null;
  display_order: number;
  is_active: boolean;
}

interface TeamMemberManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: TeamMember | null;
  onSuccess: () => void;
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE = 5 * 1024 * 1024; // 5MB

export function TeamMemberManagementDialog({
  open,
  onOpenChange,
  member,
  onSuccess,
}: TeamMemberManagementDialogProps) {
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    credentials: '',
    bio: '',
    photo_url: '',
    linkedin_url: '',
    display_order: 0,
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (member) {
      setFormData({
        name: member.name,
        title: member.title,
        credentials: member.credentials || '',
        bio: member.bio,
        photo_url: member.photo_url,
        linkedin_url: member.linkedin_url || '',
        display_order: member.display_order,
        is_active: member.is_active,
      });
    } else {
      setFormData({
        name: '',
        title: '',
        credentials: '',
        bio: '',
        photo_url: '',
        linkedin_url: '',
        display_order: 0,
        is_active: true,
      });
    }
  }, [member, open]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a JPEG, PNG, or WebP image.',
        variant: 'destructive',
      });
      e.target.value = '';
      return;
    }

    if (file.size > MAX_SIZE) {
      toast({
        title: 'File too large',
        description: 'Maximum photo size is 5MB.',
        variant: 'destructive',
      });
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const filename = `${crypto.randomUUID()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('team-photos')
        .upload(filename, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type,
        });

      if (uploadError) throw uploadError;

      const { data: publicData } = supabase.storage
        .from('team-photos')
        .getPublicUrl(filename);

      setFormData((prev) => ({ ...prev, photo_url: publicData.publicUrl }));

      toast({
        title: 'Photo uploaded',
        description: 'The photo will be saved when you submit the form.',
      });
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.photo_url) {
      toast({
        title: 'Photo required',
        description: 'Please upload a photo for the team member.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);

    try {
      const dataToSave = {
        name: formData.name,
        title: formData.title,
        credentials: formData.credentials || null,
        bio: formData.bio,
        photo_url: formData.photo_url,
        linkedin_url: formData.linkedin_url || null,
        display_order: formData.display_order,
        is_active: formData.is_active,
      };

      if (member) {
        const { error } = await supabase
          .from('team_members')
          .update(dataToSave)
          .eq('id', member.id);

        if (error) throw error;

        // Sync author photo (and renamed author name) on any blog posts written
        // by this team member. We match on the previous name so renames carry over.
        const photoChanged = member.photo_url !== formData.photo_url;
        const nameChanged = member.name !== formData.name;
        if (photoChanged || nameChanged) {
          const { error: syncError, count } = await supabase
            .from('blog_posts')
            .update(
              {
                author_image_url: formData.photo_url,
                author_name: formData.name,
              },
              { count: 'exact' }
            )
            .eq('author_name', member.name);

          if (syncError) {
            console.error('Failed to sync author info on blog posts:', syncError);
            toast({
              title: 'Photo updated, but blog posts not synced',
              description: syncError.message,
              variant: 'destructive',
            });
          } else if (count && count > 0) {
            toast({
              title: 'Blog posts updated',
              description: `Synced author info on ${count} blog post${count === 1 ? '' : 's'}.`,
            });
          }
        }

        toast({
          title: 'Success',
          description: 'Team member updated successfully',
        });
      } else {
        const { error } = await supabase
          .from('team_members')
          .insert(dataToSave);

        if (error) throw error;

        toast({
          title: 'Success',
          description: 'Team member added successfully',
        });
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {member ? 'Edit Team Member' : 'Add Team Member'}
          </DialogTitle>
          <DialogDescription>
            {member
              ? 'Update the team member details below.'
              : 'Add a new team member to the leadership section.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., John Smith"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Founder & CEO"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="credentials">Credentials</Label>
            <Input
              id="credentials"
              value={formData.credentials}
              onChange={(e) => setFormData({ ...formData, credentials: e.target.value })}
              placeholder="e.g., CISSP, CISO | MSc Cyber Security"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bio">Biography *</Label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="Write a brief biography..."
              rows={4}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Photo *</Label>
            <div className="flex items-start gap-4">
              <div className="h-24 w-24 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
                {formData.photo_url ? (
                  <img
                    src={formData.photo_url}
                    alt="Team member preview"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                    No photo
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      {formData.photo_url ? 'Replace Photo' : 'Upload Photo'}
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground">
                  JPEG, PNG, or WebP. Max 5MB.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="linkedin_url">LinkedIn URL</Label>
            <Input
              id="linkedin_url"
              value={formData.linkedin_url}
              onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
              placeholder="e.g., https://linkedin.com/in/johnsmith"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="display_order">Display Order</Label>
              <Input
                id="display_order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                min={0}
              />
              <p className="text-xs text-muted-foreground">
                Lower numbers appear first
              </p>
            </div>

            <div className="space-y-2">
              <Label>Visibility</Label>
              <div className="flex items-center gap-2 pt-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <span className="text-sm text-muted-foreground">
                  {formData.is_active ? 'Visible on website' : 'Hidden from website'}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || uploading || !formData.photo_url}>
              {saving ? 'Saving...' : member ? 'Update' : 'Add'} Team Member
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
